import { Box, Chip, useTheme } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { FunctionComponent, useContext } from 'react';
import { ViewHeader } from '../../../core/components/ViewHeader/ViewHeader';
import { AppContext } from '../../../core/config';
import { useAppTranslate, useBusinessContext } from '../../../core/hooks';
import { Loader } from '../../../ui/components';
import { ItsmBusinessContextConfig } from '../../config';
import { useItsmNetworkServiceIncidents } from '../../hooks/ItsmNetworkServiceIncidentsApiGet/ItsmNetworkServiceIncidentsApiGet.hook';
import { mapOnTableChangeEventToPaginationViewModelChange } from '../../../core/model';
import { useLocalizedDateFormat } from '../../../core/hooks/DateFormat/DateFormat';

export const ItsmNetworkServiceIncidents: FunctionComponent = () => {
  useBusinessContext(ItsmBusinessContextConfig);
  const translate = useAppTranslate();
  const { customerId } = useContext(AppContext);
  const { data, isFetched, isFetching, total, pagination, setPagination } = useItsmNetworkServiceIncidents(customerId);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const localizedDateFormat = useLocalizedDateFormat();
  const renderDate = (date?: string): string => date ? localizedDateFormat.format(new Date(date)) : '-';

  const options: MUIDataTableOptions = {
    serverSide: true,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    sortOrder: pagination.sortOrder,
    count: total,
    enableNestedDataAccess: '.',
    selectToolbarPlacement: 'none',
    download: false,
    print: false,
    rowHover: false,
    expandableRows: false,
    selectableRows: 'none',
    expandableRowsHeader: false,
    filter: false,
    search: false,
    viewColumns: false,
    onTableChange: (action, tableState) => {
      const paginationChange = mapOnTableChangeEventToPaginationViewModelChange(action, tableState);
      if (paginationChange !== null) {
        setPagination(paginationChange.pagination);
      }
    }
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'itsmId',
      label: translate('networkServiceIncidents.itsmId'),
    },
    {
      name: 'status',
      label: translate('networkServiceIncidents.status'),
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <Chip
            label={data && data[dataIndex].status}
            sx={{ background: '#D7EDFF', color: primaryColor }}
          />;
        },
      },
    },
    {
      name: 'sozOrderNumber',
      label: translate('networkServiceIncidents.sozOrderNumber'),
    },
    {
      name: 'serviceName',
      label: translate('networkServiceIncidents.serviceName'),
    },
    {
      name: 'reportingChannel',
      label: translate('networkServiceIncidents.reportingChannel'),
    },
    {
      name: 'creationTime',
      label: translate('networkServiceIncidents.creationTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].creationTime);
        },
      },
    },
    {
      name: 'lastUpdateTime',
      label: translate('networkServiceIncidents.lastUpdateTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].lastUpdateTime);
        },
      },
    },
    {
      name: 'resolutionTime',
      label: translate('networkServiceIncidents.resolutionTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(data?.[dataIndex].resolutionTime);
        },
      },
    },
  ];

  return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
    <ViewHeader title={translate('networkServiceIncidents.title')} />
    { isFetching ? (<Loader loading={true} />) : null }
    { !isFetching && isFetched && data
      ? <MUIDataTable
          title=''
          columns={ columns }
          data={ data }
          options={ options }
        />
      : null
    }
  </Box>;
};